import type { SlateBlock } from '@atlas/renderer';
import { Editor, Transforms } from 'slate';
import areSamePoints from '@atlas/tool/box/slate/areSamePoints';
import extractItemsFromListBlock from '@atlas/tool/box/slate/extractItemsFromListBlock';
import isCalloutBlock from '@atlas/tool/box/slate/isCalloutBlock';
import isListBlock from '@atlas/tool/box/slate/isListBlock';
import isMultiLineTextBlock from '@atlas/tool/box/slate/isMultiLineTextBlock';
import isOneLineTextBlock, { type OneLineTextBlock } from '@atlas/tool/box/slate/isOneLineTextBlock';
import matchTopBlockNodes from '@atlas/tool/box/slate/matchTopBlockNodes';
import removeTopLevelBlocks from '@atlas/tool/box/slate/removeTopLevelBlocks';
import sortSelection from '@atlas/tool/box/slate/sortSelection';
import calculatePreviousPoint from '@atlas/tool/box/slate/calculatePreviousPoint';
import changeEditorSelection from '@atlas/tool/box/slate/changeEditorSelection';

export default function convertBlocksToOneLineTexts(
    editor: Editor,
    blockType: OneLineTextBlock['type'],
    options?: {
        match?: SlateBlock['type'] | ((node: SlateBlock) => boolean);
        blockPatch?: (block: SlateBlock) => SlateBlock;
    },
) {
    if (!editor.selection) return;

    const { anchor, focus } = editor.selection;
    const sortedPoints = sortSelection({ anchor, focus });
    const selectionStart = sortedPoints[0];

    const selectionEnd =
        areSamePoints(anchor, focus) || focus.offset
            ? sortedPoints[1]
            : calculatePreviousPoint(editor, sortedPoints[1]);

    if (!areSamePoints(sortedPoints[1], selectionEnd)) {
        changeEditorSelection(editor, {
            anchor: anchor === selectionStart ? selectionStart : selectionEnd,
            focus: focus === selectionStart ? selectionStart : selectionEnd,
        });
    }

    const match = options?.match;
    const matches = matchTopBlockNodes(editor);

    const allBlocks = matches.reduce<SlateBlock[]>((allBlocks, [block]) => {
        if (typeof match === 'string' && match !== block.type) {
            return allBlocks.concat(block);
        }

        if (typeof match === 'function' && !match(block)) {
            return allBlocks.concat(block);
        }

        if (isCalloutBlock(block)) {
            block = block.children[1];
        }

        if (isOneLineTextBlock(block)) {
            return allBlocks.concat({ type: blockType, children: block.children });
        }

        if (isMultiLineTextBlock(block)) {
            const newBlocks = block.children.reduce<OneLineTextBlock[]>(
                (newBlocks, leaf) => {
                    const [firstLine, ...lines] = leaf.text.split('\n');
                    const lastBlock = newBlocks[newBlocks.length - 1];
                    newBlocks[newBlocks.length - 1] = {
                        ...lastBlock,
                        children: [...lastBlock.children, { ...leaf, text: firstLine }],
                    };

                    if (!lines.length) return newBlocks;

                    return newBlocks.concat(
                        lines.map((text) => ({
                            type: blockType,
                            children: [{ ...leaf, text }],
                        })),
                    );
                },
                [{ type: blockType, children: [] }],
            );

            return allBlocks.concat(newBlocks);
        }

        if (isListBlock(block)) {
            return allBlocks.concat(
                extractItemsFromListBlock(block).map((listItem) => ({
                    type: blockType,
                    children: listItem.children,
                })),
            );
        }

        return allBlocks.concat(block);
    }, []);

    const patchedBlocks = options?.blockPatch ? allBlocks.map(options.blockPatch) : allBlocks;

    removeTopLevelBlocks(editor, selectionStart.path[0], selectionEnd.path[0]);
    Transforms.insertNodes(editor, patchedBlocks, { at: [selectionStart.path[0]] });

    const newSelectionStart = Editor.start(editor, [selectionStart.path[0]]);

    const newSelectionEnd = Editor.end(editor, [selectionStart.path[0] + patchedBlocks.length - 1]);

    changeEditorSelection(editor, {
        anchor: anchor === selectionStart ? newSelectionStart : newSelectionEnd,
        focus: anchor === selectionStart ? newSelectionEnd : newSelectionStart,
    });
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('convertBlocksToOneLineTexts', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
